import { AuthProvider } from 'react-admin';
import { jwtDecode } from 'jwt-decode';
import { forgetEffectiveFlags, loadEffectiveFlags } from './admin/featureFlags/featureFlagsApi';
import { forgetRoleAccess, loadRoleAccess } from './admin/roleCenter/roleAccessApi';
import { mirrorKnowsRole } from './admin/permissions';
import { usageRecorder } from './usage/usageRecorder';

const API = process.env.REACT_APP_NEST_API_URL || 'http://localhost:3001';

interface JwtPayload {
  sub: number;
  email: string;
  username: string;
  role: string;
  companyId?: number | null;
  companyName?: string | null;
  exp: number;
}

const decode = (): JwtPayload | null => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    return jwtDecode<JwtPayload>(token);
  } catch {
    return null;
  }
};

/**
 * Spółka zalogowanego konta z tokena (`companyId` w JWT, `auth.service.ts`).
 * `null` = konto bez spółki; `undefined` = nie wiemy (brak sesji albo token
 * sprzed dodania pola) — wtedy interfejs niczego na tej podstawie nie blokuje.
 */
export function sessionCompanyId(): number | null | undefined {
  const p = decode();
  if (!p || !('companyId' in p)) return undefined;
  return typeof p.companyId === 'number' ? p.companyId : null;
}

/**
 * Rola zalogowanego konta z tokena — ta sama, którą oddaje `getPermissions()`.
 * Dla miejsc poza drzewem react-admina albo tam, gdzie rola ustawia tylko
 * widok domyślny (np. „Tylko moje pojazdy” planisty). `undefined` = brak sesji.
 * O dostępie nie rozstrzyga — to robi serwer i `usePermissions`.
 */
export function sessionRole(): string | undefined {
  const role = decode()?.role;
  return typeof role === 'string' && role !== '' ? role : undefined;
}

/**
 * Identyfikator zalogowanego konta z tokena (`sub`) — klucz magazynu
 * z zapamiętanymi filtrami list (`listMemory/listMemoryStore.ts`).
 * `undefined` = brak sesji albo token bez identyfikatora.
 */
export function sessionUserId(): number | undefined {
  const sub = decode()?.sub;
  return typeof sub === 'number' && Number.isFinite(sub) ? sub : undefined;
}

/** Komunikat przy 403 — sesja zostaje, brakuje uprawnienia do sekcji. */
export const FORBIDDEN_MESSAGE = 'Brak uprawnień do tej operacji';

/**
 * Komunikaty logowania (07.10.2026, lista komunikatów z 01.10: „Nieprawidłowy
 * email lub hasło” pojawiało się też przy awarii serwera i braku sieci —
 * użytkownik poprawiał hasło, które było dobre).
 */
export const LOGIN_BAD_CREDENTIALS = 'Nieprawidłowy e-mail lub hasło';
export const LOGIN_NETWORK_ERROR =
  'Brak połączenia z serwerem. Sprawdź połączenie z siecią i spróbuj ponownie.';
export const LOGIN_SERVER_ERROR =
  'Serwer nie mógł teraz sprawdzić danych logowania. Spróbuj ponownie za chwilę.';
export const LOGIN_TOO_MANY_ATTEMPTS =
  'Zbyt wiele prób logowania. Odczekaj chwilę i spróbuj ponownie.';

/**
 * Komunikat dla odmowy logowania po kodzie odpowiedzi. API odpowiada 401 na
 * złe dane (`auth.service.ts`); 400 i 403 to też odmowa dla tych danych.
 * Reszta (5xx, brama, nieznany kod) to kłopot po stronie serwera, nie hasła.
 */
export function loginFailureMessage(status: number): string {
  if (status === 400 || status === 401 || status === 403) return LOGIN_BAD_CREDENTIALS;
  if (status === 429) return LOGIN_TOO_MANY_ATTEMPTS;
  return LOGIN_SERVER_ERROR;
}

/** Przełącznik „Mój profil” — klucz z API (`feature-flags.ts`), nie tłumaczymy. */
export const OWN_PROFILE_FLAG = 'ownProfile';

/** Profil własnego konta z API (`GET /users/me`). */
export interface OwnProfileName {
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * Nazwa w pasku i menu użytkownika: imię i nazwisko, gdy wpisane w „Mój
 * profil” (wystarczy jedno z dwóch); inaczej login jak dotąd — „tylko jeśli
 * jest skonfigurowane” (decyzja użytkownika 07.10.2026). Ta sama reguła co
 * `displayName` w API (`shared/person-name.ts`).
 */
export function identityName(profile: OwnProfileName | null | undefined, login: string): string {
  const parts = [profile?.firstName, profile?.lastName]
    .filter((p): p is string => typeof p === 'string' && p.trim() !== '')
    .map((p) => p.trim());
  return parts.length > 0 ? parts.join(' ') : login;
}

export const authProvider: AuthProvider = {
  // Pole formularza react-admina nazywa się `username`, ale logujemy adresem
  // e-mail (API szuka konta po `email`) — etykieta „E-mail” w słowniku.
  login: async ({ username, password }) => {
    let res: Response;
    try {
      res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: username, password }),
      });
    } catch {
      // `fetch` odrzuca tylko wtedy, gdy odpowiedzi nie było wcale.
      throw new Error(LOGIN_NETWORK_ERROR);
    }
    if (!res.ok) {
      throw new Error(loginFailureMessage(res.status));
    }
    const { access_token } = await res.json();
    // Zdarzenia pomiaru poprzedniej sesji nie idą z tokenem nowego konta.
    usageRecorder.reset();
    localStorage.setItem('token', access_token);
    // Przełączniki funkcji to stan konta (wybór wersji) — po zmianie konta
    // czytamy je od nowa, a ekran czeka na nie (12.10.2026).
    forgetEffectiveFlags();
    // Tak samo uprawnienia z centrum ról (22.10.2026).
    forgetRoleAccess();
  },

  logout: async () => {
    // Pomiar użycia: wylogowanie i to, co zebrane, wychodzi jeszcze z tokenem tego konta.
    usageRecorder.logout();
    localStorage.removeItem('token');
    forgetEffectiveFlags();
    forgetRoleAccess();
  },

  /**
   * Błąd z API: 401 to koniec sesji, 403 — brak uprawnień przy żywej sesji.
   *
   * Do 01.10.2026 oba kody wylogowywały. Przy rolach działowych (planista
   * FTL, trader) 403 jest zwykłą odpowiedzią na sekcję innego działu —
   * wylogowanie wyrzucałoby z pracy kogoś, kto np. wpisał adres ekranu floty.
   * Odrzucenie z `logoutUser: false` to sygnał dla react-admina: pokaż
   * komunikat, nie wylogowuj i nie przekierowuj.
   */
  checkError: async (error) => {
    const status = error?.status ?? error?.response?.status;
    if (status === 401) {
      localStorage.removeItem('token');
      throw new Error('Sesja wygasła — zaloguj się ponownie');
    }
    if (status === 403) {
      throw Object.assign(new Error(FORBIDDEN_MESSAGE), { logoutUser: false });
    }
  },

  checkAuth: async () => {
    const p = decode();
    if (!p || p.exp * 1000 < Date.now()) {
      localStorage.removeItem('token');
      throw new Error('Wymagane logowanie');
    }
  },

  /**
   * Tożsamość do paska: login z tokenu, a imię i nazwisko z profilu
   * (`GET /users/me`, 07.10.2026). Token zostaje bez zmian — imię i nazwisko
   * zmieniają się w trakcie sesji („Mój profil”), a token żyje dobę.
   * Profil niedostępny (sieć, starsze API) = login, sesja działa dalej.
   *
   * Za przełącznikiem `ownProfile` (12.10.2026). Wyłączony (wersja „obecna”):
   * trasy nie ma (404), więc w ogóle o nią nie pytamy — poprawka 15.10.2026,
   * wcześniej każde wejście zostawiało 404 w konsoli. Stan przełącznika
   * bierzemy z tego samego odczytu `/feature-flags/effective`, którym front
   * i tak rysuje menu (jedno zapytanie na sesję, wspólna obietnica) —
   * czekamy na niego, więc kolejność wczytania nie ma znaczenia. Odczyt się
   * nie udał = nie wiemy = wersja „obecna”: login, bez pytania o profil.
   */
  getIdentity: async () => {
    const p = decode();
    if (!p) throw new Error('Brak sesji');
    const login = p.username || p.email;
    const flags = await loadEffectiveFlags();
    if (flags?.flags[OWN_PROFILE_FLAG] !== true) return { id: p.sub, fullName: login };
    try {
      const res = await fetch(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
      });
      if (!res.ok) return { id: p.sub, fullName: login };
      return { id: p.sub, fullName: identityName((await res.json()) as OwnProfileName, login) };
    } catch {
      return { id: p.sub, fullName: login };
    }
  },

  /**
   * Rola z tokenu. Przy włączonym centrum ról (22.10.2026, `roleCenter`)
   * uprawnienia i ukryte elementy tej roli przychodzą z API
   * (`roleCenter/roleAccessApi.ts`); menu i sekcje przeliczają się, gdy
   * przyjdą (`useRoleAccessState`). Odczyt się nie udał — lustro, jak dotąd.
   *
   * **Rola znana lustru dostaje się od razu, centrum doczytuje się w tle**
   * (poprawka 23.10.2026, smoke stagingu po #519). Wcześniej rola czekała na
   * dwa zapytania po kolei (przełączniki, potem `/role-center/me`), a przez
   * ten czas react-admin nie znał roli — menu i sekcje wyglądały jak u gościa:
   * administrator bez „Samochodów”, „Spółek” i „Przełączników funkcji”.
   * Administrator ma zawsze wszystko (centrum go nie zmienia), a dla
   * pozostałych ról lustro to dokładnie zasiew centrum. Czeka tylko rola
   * własna z centrum — lustro jej nie zna, więc bez odczytu nie ma czego
   * pokazać.
   */
  getPermissions: async () => {
    const role = decode()?.role ?? '';
    if (!role) return role;
    const access = loadRoleAccess().catch(() => null);
    if (!mirrorKnowsRole(role)) await access;
    return role;
  },
};
